package com.arena_web.ui.sections.people

import androidx.compose.runtime.*
import com.arena_web.data.dto.PersonDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import kotlin.math.abs

// We render PlayerCard at its native Large size and CSS-scale it up to match
// the People-page design. The footprint MUST be derived from the same
// PlayerCardSize.Large dimensions the card actually renders at — hardcoding a
// smaller frame (the old 238×369) made the footprint narrower than the card,
// so with transform-origin: top-left the card spilled to the right and looked
// off-centre (more gap on the left).
private const val SCALE = 1.5
private val NATIVE_W = PlayerCardSize.Large.w
private val NATIVE_H = PlayerCardSize.Large.h
private val SCALED_W = (NATIVE_W * SCALE).toInt()
private val SCALED_H = (NATIVE_H * SCALE).toInt()

// Distance (in px) the user must drag past centre before release commits the
// swipe. Below this, the card snaps back.
private const val COMMIT_THRESHOLD_PX = 120.0

// How far the card flies off-screen on commit. Drag commits push it well
// past the viewport edge to continue the user's gesture; button commits
// use a shorter throw so the click doesn't trigger a dramatic sweep.
private const val FLY_OFF_DRAG_PX = 1500.0
private const val FLY_OFF_BUTTON_PX = 480.0
private const val ROTATE_DRAG_DEG = 30.0
private const val ROTATE_BUTTON_DEG = 12.0

// Fade in the LIKE / NOPE badges fully by the time the user has dragged this
// many px in either direction (matches the commit threshold for clarity).
private const val BADGE_FULL_AT_PX = 140.0

// Animation duration in ms — long enough to read the slide, short enough to
// feel responsive when the user just wants to plough through the queue.
private const val COMMIT_ANIM_MS = 280L

// Commit source — drag-released gestures get the big sweep, button taps a
// shorter, gentler one. Kept as String tokens so the parent can stay
// stateless about which path is in flight.
const val COMMIT_LIKE = "LIKE"
const val COMMIT_SKIP = "SKIP"
const val COMMIT_LIKE_BUTTON = "LIKE_BUTTON"
const val COMMIT_SKIP_BUTTON = "SKIP_BUTTON"

private fun isLike(commit: String?) = commit == COMMIT_LIKE || commit == COMMIT_LIKE_BUTTON
private fun isSkip(commit: String?) = commit == COMMIT_SKIP || commit == COMMIT_SKIP_BUTTON
private fun isButton(commit: String?) =
    commit == COMMIT_LIKE_BUTTON || commit == COMMIT_SKIP_BUTTON

/**
 * Tinder-style card stack with drag-to-swipe. Wraps a single [PersonCard]
 * in a Div that captures pointer events; the card follows the pointer in
 * real-time with a slight rotation, and releasing past
 * [COMMIT_THRESHOLD_PX] commits the swipe (right ⇒ LIKE, left ⇒ SKIP).
 *
 * The parent owns [pendingCommit] — both this composable (on drag-release)
 * and external buttons (in `People.kt`) can set it through [onDragCommit].
 * Once set, the card animates off-screen, then [onAnimationDone] fires so
 * the parent can dispatch the actual `viewModel.like()/skip()` call.
 */
@Composable
fun PeopleCardStack(
    current: PersonDto,
    sportName: String,
    pendingCommit: String?,
    onDragCommit: (String) -> Unit,
    onAnimationDone: () -> Unit,
) {
    var dragX by remember(current.userId) { mutableStateOf(0.0) }
    var dragging by remember(current.userId) { mutableStateOf(false) }
    val dragId = remember(current.userId) {
        "people-swipe-${kotlin.random.Random.nextInt(1_000_000)}"
    }

    // Drive the off-screen animation once the parent sets pendingCommit
    // (either from a drag-release in this component or from a button click
    // in `People.kt`). After the animation completes, notify the parent so
    // it can dispatch the actual like/skip and reset the state.
    LaunchedEffect(pendingCommit, current.userId) {
        if (pendingCommit != null) {
            delay(COMMIT_ANIM_MS)
            onAnimationDone()
        }
    }

    // Visual transform — final transform depends on either a pending commit
    // (slide off-screen) or live drag (follow finger). Rotation is capped so
    // the card doesn't spin grotesquely; button commits use a softer pair
    // (shorter travel + smaller rotation) so a tap doesn't sweep across
    // the whole viewport.
    val translatePx = if (isButton(pendingCommit)) FLY_OFF_BUTTON_PX else FLY_OFF_DRAG_PX
    val rotateMax = if (isButton(pendingCommit)) ROTATE_BUTTON_DEG else ROTATE_DRAG_DEG
    val (translateX, rotateDeg, opacity) = when {
        isLike(pendingCommit) -> Triple(translatePx, rotateMax, 0.0)
        isSkip(pendingCommit) -> Triple(-translatePx, -rotateMax, 0.0)
        else -> Triple(dragX, (dragX / 12.0).coerceIn(-ROTATE_DRAG_DEG, ROTATE_DRAG_DEG), 1.0)
    }

    // Smooth easing between states, but during an active drag we want the
    // card pinned to the pointer with zero lag.
    val transition = if (dragging && pendingCommit == null) {
        "none"
    } else {
        "transform ${COMMIT_ANIM_MS}ms ease-out, opacity ${COMMIT_ANIM_MS}ms ease-out"
    }

    // Pointer events cover both mouse and touch. setPointerCapture routes
    // subsequent move/up to us even if the cursor leaves the card, so a
    // fast flick doesn't get "lost" mid-drag. Attached via raw DOM so we
    // can read the native PointerEvent fields (clientX, pointerId).
    DisposableEffect(current.userId, pendingCommit) {
        val el = document.getElementById(dragId)
        if (el == null) {
            onDispose {}
        } else {
            var startX = 0.0

            val downHandler: (Event) -> Unit = handler@{ event ->
                if (pendingCommit != null) return@handler
                val pe = event.asDynamic()
                startX = (pe.clientX as Number).toDouble()
                dragging = true
                el.asDynamic().setPointerCapture(pe.pointerId)
            }
            val moveHandler: (Event) -> Unit = handler@{ event ->
                if (!dragging) return@handler
                val pe = event.asDynamic()
                dragX = (pe.clientX as Number).toDouble() - startX
            }
            val upHandler: (Event) -> Unit = handler@{ _ ->
                if (!dragging) return@handler
                dragging = false
                val release = dragX
                // Always reset dragX after a release: if the parent accepts
                // the commit it'll set `pendingCommit` and the visual switches
                // to the fly-off transform (ignoring dragX); if it blocks the
                // commit (e.g. profile-incomplete prompt), dragX=0 makes the
                // card spring back to centre via the existing transition.
                dragX = 0.0
                when {
                    release > COMMIT_THRESHOLD_PX -> onDragCommit(COMMIT_LIKE)
                    release < -COMMIT_THRESHOLD_PX -> onDragCommit(COMMIT_SKIP)
                }
            }
            val cancelHandler: (Event) -> Unit = { _ ->
                dragging = false
                dragX = 0.0
            }

            el.addEventListener("pointerdown", downHandler)
            el.addEventListener("pointermove", moveHandler)
            el.addEventListener("pointerup", upHandler)
            el.addEventListener("pointercancel", cancelHandler)
            onDispose {
                el.removeEventListener("pointerdown", downHandler)
                el.removeEventListener("pointermove", moveHandler)
                el.removeEventListener("pointerup", upHandler)
                el.removeEventListener("pointercancel", cancelHandler)
            }
        }
    }

    Box(
        modifier = Modifier
            .sWidth(SCALED_W)
            .sHeight(SCALED_H)
            .styleModifier { property("position", "relative") },
    ) {
        Div(attrs = {
            id(dragId)
            style {
                property("position", "absolute")
                property("inset", "0")
                property("transform", "translateX(${translateX}px) rotate(${rotateDeg}deg)")
                property("transition", transition)
                property("opacity", opacity.toString())
                property("cursor", if (dragging) "grabbing" else "grab")
                property("touch-action", "none")
                property("user-select", "none")
                property("-webkit-user-select", "none")
                property("will-change", "transform")
            }
        }) {
            PersonCard(person = current, sportName = sportName)
            DragIndicators(dragX = dragX, pendingCommit = pendingCommit)
        }
    }
}

/**
 * LIKE / NOPE badges overlaid on the card while the user is dragging — fade
 * in proportionally to drag distance, fully opaque once past
 * [BADGE_FULL_AT_PX]. Helps the user feel which side commits without having
 * to memorise the threshold.
 */
@Composable
private fun DragIndicators(dragX: Double, pendingCommit: String?) {
    val t = LocalAppStrings.current
    val likeOpacity = when {
        isLike(pendingCommit) -> 1.0
        dragX > 0 -> (dragX / BADGE_FULL_AT_PX).coerceIn(0.0, 1.0)
        else -> 0.0
    }
    val skipOpacity = when {
        isSkip(pendingCommit) -> 1.0
        dragX < 0 -> (-dragX / BADGE_FULL_AT_PX).coerceIn(0.0, 1.0)
        else -> 0.0
    }

    if (likeOpacity > 0.01) {
        SwipeBadge(
            label = t.peopleBadgeLike,
            color = "#7BFA6F",
            rotation = -18,
            alignTopLeft = true,
            opacity = likeOpacity,
        )
    }
    if (skipOpacity > 0.01) {
        SwipeBadge(
            label = t.peopleBadgeNope,
            color = "#FF4242",
            rotation = 18,
            alignTopLeft = false,
            opacity = skipOpacity,
        )
    }
}

@Composable
private fun SwipeBadge(
    label: String,
    color: String,
    rotation: Int,
    alignTopLeft: Boolean,
    opacity: Double,
) {
    Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("top", s(40))
            if (alignTopLeft) property("left", s(28)) else property("right", s(28))
            property("padding", "${s(6)} ${s(18)}")
            property("border", "3px solid $color")
            property("border-radius", s(8))
            property("color", color)
            property("font-family", "Urbanist, sans-serif")
            property("font-size", s(28))
            property("font-weight", "800")
            property("letter-spacing", s(2))
            property("transform", "rotate(${rotation}deg)")
            property("opacity", opacity.toString())
            property("background-color", "rgba(0, 0, 0, 0.25)")
            property("pointer-events", "none")
            property("backdrop-filter", "blur(2px)")
        },
    ) {
        SpanText(label)
    }
}

/**
 * The big swipe card. Built from `PlayerCard.Large` so we inherit the
 * sport-specific frame + avatar rendering, then overlays the name/age,
 * city and sport tags at the bottom per Figma 7502:299510.
 *
 * The visual fields ([avatarUrlOverride], [cardAssetsBasePathOverride],
 * [cardTextColorOverride]) default to the values on [person] but can be
 * overridden so the detail-popup gallery can render the same person across
 * any of their `ProfileCardDto` instances without losing the overlay.
 */
@Composable
fun PersonCard(
    person: PersonDto,
    sportName: String,
    avatarUrlOverride: String? = null,
    cardAssetsBasePathOverride: String? = null,
    cardTextColorOverride: String? = null,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sWidth(SCALED_W)
            .sHeight(SCALED_H)
            .styleModifier { property("position", "relative") },
    ) {
        // Inner PlayerCard rendered at native pixel size, then transformed
        // to fill the scaled outer Box. transform-origin pins it to the
        // top-left so the scaled output lines up with our explicit width.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("top", "0")
                property("left", "0")
                property("width", s(NATIVE_W))
                property("height", s(NATIVE_H))
                property("transform", "scale($SCALE)")
                property("transform-origin", "top left")
            },
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Large,
                avatarUrl = avatarUrlOverride ?: person.avatarUrl,
                hideRating = true,
                // Built-in card shadow asset under the avatar/name (same
                // depth treatment used on Profile + Card Shop).
                hideShadow = false,
                cardAssetsBasePath = cardAssetsBasePathOverride ?: person.cardAssetsBasePath,
                cardTextColor = cardTextColorOverride ?: person.cardTextColor,
            )
        }

        // Name / city / sport tags overlay — sits in the lower third of the
        // card frame so it doesn't crash into the avatar above or the FUT
        // frame's decorative tail below.
        Column(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("left", "0")
                    property("right", "0")
                    // Raised from 60→96 so the name/city/tags block sits
                    // higher above the FUT frame's curved bottom and stops
                    // clipping into it on the Large card scale.
                    property("bottom", s(96))
                    property("pointer-events", "none")
                }
                .sGap(8),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.peopleNameAge(person.fullName.firstNameUpper(), person.age),
                modifier = Modifier
                    .sFontSize(32)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(38)
                    .sLetterSpacing(0.64)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb(0x00, 0x61, 0xFF))
                    .styleModifier {
                        property("text-align", "center")
                        property("text-transform", "uppercase")
                        property("text-shadow", "0 2px 8px rgba(0, 97, 255, 0.10)")
                        property("-webkit-text-stroke", "0.6px rgba(0, 97, 255, 0.10)")
                        property("paint-order", "stroke fill")
                    },
            )
            Row(
                modifier = Modifier.sGap(6),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(src = AppIcons.Location, modifier = Modifier.sSize(20))
                SpanText(
                    person.city,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .color(AppColors.Text.Secondary)
                        .styleModifier { property("text-shadow", "0 2px 6px rgba(0, 0, 0, 0.5)") },
                )
            }
            // First N interests across ALL categories in the order the user
            // added them (sports first, then activities, then lifestyle —
            // each list is preserved in selection order on the backend).
            // Sports keep their sport-themed colour; everything else falls
            // back to the secondary text colour.
            val sportIds = person.interestSports.toSet()
            val topInterests = (
                person.interestSports +
                    person.interestActivities +
                    person.interestLifestyle
            ).take(MAX_INTEREST_TAGS)
            if (topInterests.isNotEmpty()) {
                Row(
                    modifier = Modifier.sGap(4),
                    horizontalArrangement = Arrangement.Center,
                ) {
                    topInterests.forEach { id ->
                        val isSport = id in sportIds
                        InterestTag(
                            label = id.replaceFirstChar { it.uppercase() },
                            color = if (isSport) SportColors.forSport(id).primary
                                    else AppColors.Text.Secondary,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun InterestTag(label: String, color: CSSColorValue) {
    // Mirrors the `ChipPill` pattern from `OverviewSidebar` — outer Box owns
    // size + position-relative + background; padding/centering live in an
    // inner Row with `fillMaxHeight`. Putting padding/flex directly on the
    // outer Box was leaving the absolute `GradientBorderRing` shorter than
    // the chip's rendered height.
    Box(
        modifier = Modifier
            .sHeight(32)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", "rgba(11, 22, 46, 0.85)")
                property("backdrop-filter", "blur(4px)")
            },
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sPadding(0, 14),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(15)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.15)
                    .color(color),
            )
        }
    }
}

private fun String.firstNameUpper(): String {
    val first = trim().split(" ").firstOrNull().orEmpty()
    return first.uppercase()
}

@Suppress("unused")
private fun absHelper(x: Double): Double = abs(x)

private const val MAX_INTEREST_TAGS = 3
